import { QueryClient } from '@tanstack/react-query'
import type { QueryClientConfig } from '@tanstack/react-query'

import { isTestEnv } from './env'

/**
 * Creates a QueryClient with Help Scout defaults.
 *
 * Always sets `networkMode: 'always'` on queries and mutations to work around
 * Chrome/Safari incorrectly reporting `navigator.onLine = false` (JS-1891).
 * Custom options are deep-merged with defaults; `networkMode` cannot be overridden.
 *
 * Mutations intentionally have no `retry` default so TanStack's `retry: 0`
 * applies — retrying non-idempotent writes can cause duplicate side effects.
 */
export function createQueryClient(options?: QueryClientConfig): QueryClient {
  const isTest = isTestEnv()

  const defaults: QueryClientConfig = {
    defaultOptions: {
      queries: {
        retry: isTest ? 0 : 1,
        networkMode: 'always',
      },
      mutations: {
        networkMode: 'always',
      },
    },
  }

  return new QueryClient(mergeConfig(defaults, options))
}

function mergeConfig(
  defaults: QueryClientConfig,
  custom?: QueryClientConfig
): QueryClientConfig {
  if (!custom) return defaults

  return {
    ...defaults,
    ...custom,
    defaultOptions: {
      ...defaults.defaultOptions,
      ...custom.defaultOptions,
      queries: {
        ...defaults.defaultOptions?.queries,
        ...custom.defaultOptions?.queries,
        networkMode: 'always',
      },
      mutations: {
        ...defaults.defaultOptions?.mutations,
        ...custom.defaultOptions?.mutations,
        networkMode: 'always',
      },
    },
  }
}

export type { QueryClientConfig }
